import { lineItemSchema } from "@redotech/redo-billing-common/service-invoice";
import * as z from "zod";
import {
  billingChargeTypeSchema,
  transactionStatusSchema,
} from "./get-billing-charges-and-transactions";
import { billSchema } from "./get-bills";

const aggregatedChargeSchema_ = z.object({
  recordType: z.literal("charge"),
  type: billingChargeTypeSchema,
  chargeSubtype: z.string().nullish(),
  product: z.string().nullish(),
  totalAmount: z.number(),
  currency: z.string(),
  localTotalAmount: z.number().nullish(),
  count: z.number(),
  // Product-aware breakdown sums attached by the SVI → bill projector
  // (`enrichChargeFromBreakdowns`). Each field is non-null only when
  // the underlying charges actually carried data for it.
  includedCount: z.number().nullish(),
  overageCount: z.number().nullish(),
  orderCount: z.number().nullish(),
  smsCount: z.number().nullish(),
  mmsCount: z.number().nullish(),
  carrierFeesMicros: z.number().nullish(),
  recoveredRevenue: z.number().nullish(),
  revenueSharePercentage: z.number().nullish(),
  channel: z.string().nullish(),
  campaignId: z.string().nullish(),
  // Verbatim legacy LineItem persisted on the source charge — carries the
  // marketing-grade per-line detail (name, date range, "(X in plan)") the
  // modal prefers over the generic charge-type label. Null for returns/shipping.
  lineItem: lineItemSchema.nullish(),
  revShareAmount: z.number().nullish(),
});
export const aggregatedChargeSchema: z.ZodType<AggregatedChargeSchema> =
  aggregatedChargeSchema_;
export interface AggregatedChargeSchema extends z.infer<
  typeof aggregatedChargeSchema_
> {}

const aggregatedTransactionSchema_ = z.object({
  recordType: z.literal("transaction"),
  status: transactionStatusSchema,
  totalAmount: z.number(),
  currency: z.string(),
  count: z.number(),
});
export const aggregatedTransactionSchema: z.ZodType<AggregatedTransactionSchema> =
  aggregatedTransactionSchema_;
export interface AggregatedTransactionSchema extends z.infer<
  typeof aggregatedTransactionSchema_
> {}

const ledgerEntrySchema = z.union([
  aggregatedChargeSchema,
  aggregatedTransactionSchema,
]);
export type LedgerEntry = z.infer<typeof ledgerEntrySchema>;

const billingTransactionSchema_ = z.object({
  id: z.string(),
  amount: z.number(),
  currency: z.string(),
  status: transactionStatusSchema,
  shopifyChargeId: z.string().nullish(),
  stripeChargeId: z.string().nullish(),
  failureMessage: z.string().nullish(),
  createdAt: z.date(),
});
export const billingTransactionSchema: z.ZodType<BillingTransactionSchema> =
  billingTransactionSchema_;
export interface BillingTransactionSchema extends z.infer<
  typeof billingTransactionSchema_
> {}

const getBillDetailsInput_ = z.object({ billId: z.string() });
export const getBillDetailsInput: z.ZodType<GetBillDetailsInput> =
  getBillDetailsInput_;
export interface GetBillDetailsInput extends z.infer<
  typeof getBillDetailsInput_
> {}

const getBillDetailsOutput_ = z.object({
  bill: billSchema,
  ledgerEntries: z.array(ledgerEntrySchema),
  transactions: z.array(billingTransactionSchema),
});
export const getBillDetailsOutput: z.ZodType<GetBillDetailsOutput> =
  getBillDetailsOutput_;
export interface GetBillDetailsOutput extends z.infer<
  typeof getBillDetailsOutput_
> {}

export const getBillDetails = {
  name: "getBillDetails" as const,
  input: getBillDetailsInput,
  output: getBillDetailsOutput,
};
